import { useApolloClient } from '@apollo/client'

import { useUserContext } from '@/contexts/UserContext.tsx'
import { getMutation } from '@/requests/apollo/mutation.ts'
import { STORY_COMMAND_META } from '@/requests/apollo/schema.ts'
import { getExecuteTemplate } from '@/requests/apollo/templates.ts'
import { useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { useQueryStory } from '@/requests/queries/useQueryStory.ts'
import { getNow } from '@/utils/date.ts'
import { addCacheListElement, getStory, removeCacheListElement } from '../apollo/cache.ts'

export const useRemoveStory = (storyId: string) => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const { projectMeta } = useQueryProjectMeta()
  const { cache } = useApolloClient()
  const { story } = useQueryStory(storyId)

  const [execute] = getMutation('Story', STORY_COMMAND_META)

  return () => {
    const requestedStory = getStory(storyId)
    if (!requestedStory) return Promise.reject(new Error(`Story with id ${storyId} not found`))

    for (const label of story.labels ?? []) {
      cache.modify({
        id: cache.identify({ __typename: 'Label', id: label.id }),
        fields: {
          count: () => label.count - 1,
        },
      })
    }

    const { variables, optimisticResponse } = getExecuteTemplate<Story>(
      projectMeta,
      'STORY_DELETE',
      user.id,
      {
        id: storyId,
      },
      [
        {
          ...requestedStory,
          updatedAt: getNow(),
          deletedAt: getNow(),
        },
      ],
    )

    return execute({
      variables,
      optimisticResponse,
      update: (cache, { data: { executeCommand } }) => {
        for (const deletedStory of executeCommand.data) {
          removeCacheListElement(
            cache.identify({ __typename: 'Project', id: projectMeta.id }),
            'stories',
            deletedStory.id,
          )
        }
      },
      onCompleted: () => {
        cache.evict({ id: cache.identify(requestedStory) })
      },
      onError: (error) => {
        console.error('Error deleting story:', error)
        if (story) return

        addCacheListElement(cache.identify({ __typename: 'Project', id: projectMeta.id }), requestedStory, 'stories')
      },
    })
  }
}
